// Документът на доставка отвсякъде (справката, началният екран, плащането):
// модалът, анулирането с причина, редакцията, отварянето на свързан документ
// по id (фактура ↔ стокови) и действията по плащането и по стоковата
// (`useDeliveryDocumentActions`). Връща `open`, `edit`/`annul` за менюто „⋯"
// на справката, `closeNotes` за началния екран и модалите за рендер.
import { useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDelivery, type Delivery } from "../../../../api/inventory";
import { useAnnulDelivery } from "../../../../hooks/useInventoryDeliveries";
import { AnnulDocumentModal } from "../../components/AnnulDocumentModal";
import {
  annulUndoNote,
  productLineItems,
  useProductById,
} from "../../components/confirm/confirmPreviews";
import { useActionToast } from "../../components/useActionToast";
import { DeliveryDocumentModal } from "./DeliveryDocumentModal";
import { docTypeOf, isDeliveryAnnulled, noteInvoiceStateOf } from "./deliveryMeta";
import { useDeliveryDocumentActions } from "./useDeliveryDocumentActions";

type OpenDocument = { delivery: Delivery; editable: boolean; autoPrint: boolean };

export const useDeliveryDocument = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const showToast = useActionToast();
  const annulMutation = useAnnulDelivery();
  const productById = useProductById();
  const [doc, setDoc] = useState<OpenDocument | null>(null);
  const [annulTarget, setAnnulTarget] = useState<Delivery | null>(null);

  // Пълният документ по id → в отворения модал, ако е същият
  const reload = (id: number) =>
    getDelivery(id)
      .then((full) =>
        setDoc((current) =>
          current && current.delivery.id === full.id ? { ...current, delivery: full } : current,
        ),
      )
      .catch(() => {
        /* остава версията, която е */
      });
  const actions = useDeliveryDocumentActions(reload);

  // Бързо от реда, после ъпгрейд до пълния документ (редовете, връзките)
  const open = (delivery: Delivery, options?: { editable?: boolean; autoPrint?: boolean }) => {
    setDoc({
      delivery,
      editable: options?.editable ?? true,
      autoPrint: options?.autoPrint ?? false,
    });
    void reload(delivery.id);
  };

  const openById = (id: number) =>
    getDelivery(id)
      .then((delivery) => setDoc({ delivery, editable: true, autoPrint: false }))
      .catch(() => message.error(t("inventoryPage.errLoad")));

  const edit = (delivery: Delivery) => {
    setDoc(null);
    navigate(`/inventory/delivery/${delivery.id}/edit`);
  };

  const confirmAnnul = (reason: string | null) => {
    if (!annulTarget) return;
    annulMutation.mutate(
      { id: annulTarget.id, reason },
      {
        onSuccess: () => {
          showToast({
            text: t("inventoryPage.annulledToast", { number: annulTarget.invoiceNumber || annulTarget.id }),
          });
          setAnnulTarget(null);
          setDoc(null);
        },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.errDelete"));
        },
      },
    );
  };

  // Фактура над стокови: собствените редове излизат, стоковите остават
  // заприходени и се връщат в „Стокови без фактура" — казваме го точно
  const annulText = (delivery: Delivery) => {
    const noteCount = delivery.linkedNoteIds?.length ?? 0;
    if (docTypeOf(delivery) === "note" || noteCount === 0) return t("inventoryPage.deliveryAnnulText");
    const ownLines = delivery.lines?.length ?? 0;
    const own = ownLines
      ? t("inventoryPage.deliveryAnnulOwnLines", { count: ownLines })
      : t("inventoryPage.deliveryAnnulNoOwnLines");
    return `${own}; ${t("inventoryPage.deliveryAnnulNotesStay", { count: noteCount })}. ${t("inventoryPage.deliveryAnnulStays")}`;
  };

  // Действията по документа — само отворен от ред и активен
  const current = doc?.editable && !isDeliveryAnnulled(doc.delivery) ? doc.delivery : null;
  const noteState = current ? noteInvoiceStateOf(current) : null;

  const modals = (
    <>
      <DeliveryDocumentModal
        open={!!doc}
        delivery={doc?.delivery ?? null}
        autoPrint={doc?.autoPrint}
        onClose={() => setDoc(null)}
        onOpenDelivery={openById}
        onEdit={doc?.editable ? () => edit(doc.delivery) : undefined}
        onAnnul={doc?.editable ? () => setAnnulTarget(doc.delivery) : undefined}
        onMarkPaid={current ? () => actions.startPay(current) : undefined}
        onChangePayment={current ? () => actions.startPay(current) : undefined}
        onMarkUnpaid={current ? () => void actions.markUnpaid(current) : undefined}
        onDetach={current && noteState === "inInvoice" ? () => void actions.detach(current) : undefined}
        onCloseWithoutInvoice={
          current && noteState === "waiting" ? () => actions.startClose([current]) : undefined
        }
        onReopen={current && noteState === "closed" ? () => actions.reopen(current) : undefined}
      />
      <AnnulDocumentModal
        open={!!annulTarget}
        title={
          annulTarget?.supplierName
            ? t("inventoryPage.cfmAnnulDeliveryTitleSupplier", {
                number: annulTarget.invoiceNumber || annulTarget.id,
                supplier: annulTarget.supplierName,
              })
            : t("inventoryPage.cfmAnnulDeliveryTitle", {
                number: annulTarget?.invoiceNumber || annulTarget?.id || "",
              })
        }
        text={annulTarget ? annulText(annulTarget) : ""}
        confirmLabel={t("inventoryPage.cfmAnnulDeliveryConfirm")}
        preview={
          annulTarget?.lines?.length
            ? {
                caption: t("inventoryPage.cfmAnnulDeliveryCaption"),
                items: productLineItems(annulTarget.lines, productById),
              }
            : undefined
        }
        note={annulUndoNote(t)}
        saving={annulMutation.isPending}
        onCancel={() => setAnnulTarget(null)}
        onConfirm={confirmAnnul}
      />
      {actions.modals}
    </>
  );

  return {
    open,
    openById,
    edit,
    annul: (delivery: Delivery) => setAnnulTarget(delivery),
    closeNotes: actions.startClose,
    modals,
  };
};
